import { useQueryClient } from "@tanstack/react-query";
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { callTabTitle } from "@/components/app/messaging-call/call-tab-title";
import { Button } from "@/components/ui/button";
import { useCall } from "@/lib/messaging/calls/call-context";
import { CALL_TAB_NAME, callTabSearch, readCallTabSearch } from "@/lib/messaging/calls/call-tab";
import { usePolicy } from "@/lib/policy";

/* A call's own tab: no app around it, the call drawn over this page. Opened by a Call, Join or
   Answer in another tab; reloading it puts the person back in the call. */
export const Route = createFileRoute("/_authenticated/call/$conversationId")({
  validateSearch: readCallTabSearch,
  component: CallTabPage,
});

function CallTabPage() {
  const { conversationId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { me } = usePolicy();
  const { startCall, joinCall, active, knocking } = useCall();
  const opened = useRef(false);
  const [tried, setTried] = useState(false);
  const [wasIn, setWasIn] = useState(false);

  // The name is how calls find this tab from the one that opened it. Leaving the call's page
  // (Back to the conversation) drops it, or the next Call would load over this tab.
  useEffect(() => {
    window.name = CALL_TAB_NAME;
    return () => {
      window.name = "";
    };
  }, []);

  useEffect(() => {
    // Once per tab: React runs effects twice in development, and a second join would replace it.
    if (opened.current) return;
    opened.current = true;
    const answer = search.answer ? { video: search.answer === "video" } : undefined;
    // The name stays out of the address, and this new tab has nothing cached: ask for it.
    const opening = callTabTitle(queryClient, conversationId, search.meetingId, me.user.id).then(
      (title) =>
        search.callId
          ? joinCall({ callId: search.callId, conversationId, title, answer })
          : startCall(conversationId, { meetingId: search.meetingId, title }),
    );
    void opening.finally(() => setTried(true));
  }, [conversationId, joinCall, me.user.id, queryClient, search, startCall]);

  // The address follows the call this tab holds (a switch, a started call's id), so a reload
  // comes back to it rather than to the first one.
  useEffect(() => {
    if (!active || (active.conversationId === conversationId && active.callId === search.callId)) {
      return;
    }
    void navigate({
      to: "/call/$conversationId",
      params: { conversationId: active.conversationId },
      search: callTabSearch({ conversationId: active.conversationId, callId: active.callId }),
      replace: true,
    });
  }, [active, conversationId, navigate, search.callId]);

  if (active && !wasIn) setWasIn(true);

  return (
    <main className="bg-call-stage text-call-stage-foreground flex min-h-dvh flex-col items-center justify-center gap-4 p-6 text-center">
      <p className="text-lg">
        {active
          ? null
          : knocking
            ? "Waiting to be let in"
            : wasIn
              ? "You left the call."
              : tried
                ? "Not in the call."
                : "Opening the call"}
      </p>
      {!active && !knocking && (wasIn || tried) ? (
        <Button asChild variant="secondary">
          <Link to="/messaging/$conversationId" params={{ conversationId }}>
            Back to the conversation
          </Link>
        </Button>
      ) : null}
    </main>
  );
}
